import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { DEFAULT_RANGE, RANGE_MAX_ISO, clampRangeIso, refreshDateIso } from "@/lib/utils/daylight-insight/dates";
import type { DetailTableSheet } from "@/lib/utils/daylight-insight/export";
import { HH, HOS } from "@/lib/utils/daylight-insight/metrics";
import { SECTIONS_HH, SECTIONS_HOS, SNAP_HH, SNAP_HOS, HERO_HH, HERO_HOS } from "@/lib/utils/daylight-insight/sections";
import { allowedPillarsFor, pillarAllowed, pillarKey } from "@/lib/utils/daylight-insight/pillars";
import { filterOptionNode, resolveDashboardSpecs, type DashboardSpec, type FilterSpec } from "@/lib/utils/daylight-insight/dashboardSpec";
import type { DateRange, PillarTab, Registry, Section, Service } from "@/lib/utils/daylight-insight/types";
import { useDashboardConfig } from "@/components/daylight-insight/api/useDashboardConfig";
import type { BranchOption, ConfigOption, DashboardDefinition, DashboardMetric, FilterOption, UserRoleOption } from "@/components/daylight-insight/api/types";

/**
 * Shared store for the insight experience. Lifts what used to be the
 * single-file `App` component's state so it persists across the three routes
 * (guide / dashboard / all-metrics).
 *
 * It is also where the post-login bootstrap call lives: mounting the provider
 * fires `GET dashboard/config` once, which supplies the service lines, every
 * filter's dropdown options, the roles they may view as, and which
 * dashboards (= metric categories) each role may open. Everything downstream
 * reads those off the store rather than a constants file — in particular, role
 * access is entirely server-driven, so granting a role a dashboard needs no
 * frontend change.
 */
/** What the header's Export button writes: the title of the dashboard on screen
 *  plus the live API metrics it is rendering. */
export interface ActiveDashboard {
  title: string;
  metrics: DashboardMetric[];
}

/** Yields the detail table on screen (By Branch / By Clinician) as an export
 *  sheet, or null when there is nothing to write. */
export type DetailTableSource = () => DetailTableSheet | null;

export interface DaylightStore {
  service: Service;
  setService: (s: Service) => void;
  /** The RoleID currently being viewed as (`UserRoles[].id`). */
  roleId: number | string | null;
  setRoleId: (id: number | string) => void;
  branches: string[];
  setBranches: (b: string[]) => void;
  payors: string[];
  setPayors: (p: string[]) => void;
  episodic: string;
  setEpisodic: (v: string) => void;
  compare: boolean;
  setCompare: (v: boolean) => void;
  range: DateRange;
  setRange: (r: DateRange) => void;
  reg: Registry;
  sections: Section[];
  snap: Section;
  hero: string[];
  snapAllowed: boolean;
  allSections: Section[];
  allSnap: Section;
  metricPillarMap: Record<string, string>;
  accessList: string[];
  drillStack: string[];
  setDrillStack: (s: string[]) => void;
  onDrill: (id: string) => void;
  closeDrill: () => void;
  /** Operating-view active pillar tab — lifted so it survives route changes. */
  pillar: string;
  setPillar: (p: string) => void;

  /** The live metrics of the dashboard (pillar tab) currently on screen,
   *  published by the page that fetched them so the header's Export button can
   *  write exactly what the user is looking at instead of a fixed workbook.
   *  Null on tabs with no live metrics (Guide, All Metrics, Current State). */
  activeDashboard: ActiveDashboard | null;
  setActiveDashboard: (d: ActiveDashboard | null) => void;
  /** Registers a detail table on screen as an export source, under an id unique
   *  to that table instance — a dashboard can carry several. Kept in a ref
   *  rather than state so a table can re-register freely as its rows, sort or
   *  visible columns change without re-rendering the tree. null unregisters
   *  (on unmount), so a dashboard with no detail table exports metrics alone. */
  registerDetailTable: (id: string, get: DetailTableSource | null) => void;
  /** The registered detail tables in on-screen (registration) order, evaluated
   *  at export-click time so each sheet matches exactly what the screen shows. */
  getDetailTables: () => DetailTableSheet[];

  // ─── Live config (GET dashboard/config) ────────────────────────────────────
  configLoading: boolean;
  configError: string | null;
  refetchConfig: () => void;
  /** Service lines this user has, e.g. HOME HEALTH / HOSPICE. */
  serviceLines: ConfigOption[];
  /** SystemID of the selected service line — the parameter the metric SPs want. */
  serviceLineSystemID: string;
  /** A filter's dropdown options for the SELECTED service line: the config
   *  node named by the filter's `key` — or `source + Key` when it carries a
   *  `source` (`{ key: "branch", source: "productivity" }` → `productivityBranch`).
   *  `value` is what the metric API takes, `label` what the dropdown shows.
   *  Empty when the config sends no such node. */
  filterOptionsFor: (f: Pick<FilterSpec, "key" | "source">) => FilterOption[];
  /** Whether the config sends a node for this filter at all — a filter without
   *  one has nothing to pick from, so it gets no control. */
  hasFilterOptions: (f: Pick<FilterSpec, "key" | "source">) => boolean;
  /** The user's branches for the SELECTED service line (the `branch` node) —
   *  for the surfaces that pick a branch outside the filter bar (Compare,
   *  Charlie). Metric calls send `value`; the UI displays on `label`. */
  branchOptions: BranchOption[];
  /** Roles this user may view as — the "Viewing as" picker's options. */
  roles: UserRoleOption[];
  /** Display name of the selected role. */
  roleName: string;
  /** The dashboards (= metric categories) granted to the SELECTED role — the
   *  raw `config.dashboards` rows. For rendering, use `dashboardSpecs`/`specFor`. */
  dashboards: DashboardDefinition[];
  /** One fully resolved render spec per granted dashboard, in the server's
   *  order — the Operating View's whole configuration: filters, detail table,
   *  drill views, extra cards. Server-sent where `config.dashboards` describes
   *  a dashboard, this build's defaults where it doesn't. */
  dashboardSpecs: DashboardSpec[];
  /** The spec for one tab key, or null when nothing is granted under it. */
  specFor: (key: string) => DashboardSpec | null;
  /** Pillars the selected role may see. `null` = grants unknown (config not in
   *  yet / failed), which callers treat as "don't gate". */
  allowedPillars: Set<string> | null;
  /** Pillar tabs for the Operating View, derived from the role's grants. */
  tabs: PillarTab[];
  /** Warehouse's last refresh, verbatim from the SP. */
  dataRefreshDate: string | null;
  /** Latest day any date picker may select, as YYYY-MM-DD: the warehouse
   *  refresh date, since nothing after it has data behind it yet. Falls back to
   *  today while the config is in flight or when the SP sends no refresh
   *  stamp. */
  maxDateIso: string;
  /** Resolve a branch's display label back to its BranchSystemID. */
  branchIdFor: (label: string) => string | undefined;
}

const Store = createContext<DaylightStore | null>(null);
export const useStore = (): DaylightStore => {
  const ctx = useContext(Store);
  if (!ctx) throw new Error("useStore must be used within a DaylightStoreProvider");
  return ctx;
};

/** The SP names service lines by description ("HOME HEALTH" / "HOSPICE"); the UI
 *  keys them as hh/hos. */
const serviceOf = (label: string): Service =>
  label.toUpperCase().includes("HOSPICE") ? "hos" : "hh";

export function DaylightStoreProvider({ children }: { children: ReactNode }) {
  const { data: config, loading: configLoading, error: configError, refetch: refetchConfig } =
    useDashboardConfig();

  const [service, setService] = useState<Service>("hh");
  const [roleId, setRoleId] = useState<number | string | null>(null);
  const [branches, setBranches] = useState<string[]>([]);
  const [payors, setPayors] = useState<string[]>([]);
  const [episodic, setEpisodic] = useState("all");
  const [compare, setCompare] = useState(false);
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [drillStack, setDrillStack] = useState<string[]>([]);
  const [pillar, setPillarState] = useState("");
  // Published by whichever page owns live metrics; the setter is the raw state
  // setter so the registering effect has a stable dependency.
  const [activeDashboard, setActiveDashboard] = useState<ActiveDashboard | null>(null);
  // The detail table on screen, registered by the table itself. A ref, not state:
  // the grid is only ever read on an Export click, so re-registering it after
  // every render costs nothing and can never loop.
  // A Map keeps insertion order, and re-registering an existing id keeps its
  // slot — so the order is the order the tables first mounted, i.e. the order
  // they render in.
  const detailTablesRef = useRef(new Map<string, DetailTableSource>());
  const registerDetailTable = useCallback((id: string, get: DetailTableSource | null) => {
    if (get) detailTablesRef.current.set(id, get);
    else detailTablesRef.current.delete(id);
  }, []);
  const getDetailTables = useCallback(
    () =>
      [...detailTablesRef.current.values()]
        .map((get) => get())
        .filter((t): t is DetailTableSheet => t !== null),
    [],
  );
  // The page's filters are its own local state, so switching dashboards resets
  // them via the page's `key` remount (see DashboardInsight).
  const setPillar = useCallback((p: string) => {
    setPillarState(p);
  }, []);

  const serviceLines = useMemo(() => config?.serviceLines ?? [], [config]);
  const roles = useMemo(() => config?.roles ?? [], [config]);

  // Land on the first role the SP returned, and recover if the selected role
  // disappears from a later config (the picker must never sit on a dead RoleID).
  useEffect(() => {
    if (!roles.length) return;
    if (roles.some((r) => r.id === roleId)) return;
    setRoleId(roles[0].id);
  }, [roles, roleId]);

  const roleName = useMemo(
    () => roles.find((r) => r.id === roleId)?.name ?? "",
    [roles, roleId],
  );

  // The dashboards granted to the selected role — this IS the access model.
  // Compared as strings: `roles[].id` and `dashboards[].roleId` come from
  // different SP result sets and needn't agree on number vs string.
  const dashboards = useMemo(
    () => (config?.dashboards ?? []).filter((d) => String(d.roleId) === String(roleId)),
    [config, roleId],
  );
  // Once the config call returns, `config` is non-null — that's how we tell
  // "no grants because it hasn't loaded" (degrade open) apart from "the server
  // granted this user nothing" (gate closed: only Current State survives).
  const configLoaded = config != null;
  const allowedPillars = useMemo(
    () => allowedPillarsFor(dashboards.map((d) => ({ label: d.label ?? "" })), configLoaded),
    [dashboards, configLoaded],
  );

  // Land on a service line the user actually has — a hospice-only user must not
  // sit on an empty Home Health view.
  useEffect(() => {
    if (!serviceLines.length) return;
    if (serviceLines.some((sl) => serviceOf(sl.label) === service)) return;
    setService(serviceOf(serviceLines[0].label));
  }, [serviceLines, service]);

  const serviceLineSystemID = useMemo(
    () => serviceLines.find((sl) => serviceOf(sl.label) === service)?.value ?? "",
    [serviceLines, service],
  );

  // A filter's options, scoped to the selected service line: a row carrying
  // `ServiceLineSystemID` belongs to that line only (a branch can belong to more
  // than one — the corporate branch does), a row without one applies to all.
  // De-duped on label, which is what the dropdown lists and selects by.
  const filterOptionsFor = useCallback(
    (f: Pick<FilterSpec, "key" | "source">): FilterOption[] => {
      const all = config?.filterOptions?.[filterOptionNode(f)] ?? [];
      const seen = new Set<string>();
      return all.filter((o) => {
        const line = o.ServiceLineSystemID;
        if (serviceLineSystemID && line && line !== serviceLineSystemID) return false;
        if (seen.has(o.label)) return false;
        seen.add(o.label);
        return true;
      });
    },
    [config, serviceLineSystemID],
  );

  const hasFilterOptions = useCallback(
    (f: Pick<FilterSpec, "key" | "source">) =>
      Array.isArray(config?.filterOptions?.[filterOptionNode(f)]),
    [config],
  );

  const branchOptions = useMemo<BranchOption[]>(
    () =>
      filterOptionsFor({ key: "branch" }).map((o) => ({
        value: o.value,
        label: o.label,
        ...(typeof o.BranchCode === "string" ? { branchCode: o.BranchCode } : {}),
      })),
    [filterOptionsFor],
  );
  const branchIdFor = (label: string) => branchOptions.find((b) => b.label === label)?.value;

  // The reportable window's upper bound. The config arrives after the default
  // range is already in state, so the range is pulled back the moment the
  // refresh date lands — a no-op once it sits inside the window, which is why
  // it can safely re-run rather than only fixing the very first value.
  const maxDateIso = refreshDateIso(config?.dataRefreshDate) ?? RANGE_MAX_ISO;
  useEffect(() => {
    setRange((r) => clampRangeIso(r, maxDateIso));
  }, [maxDateIso]);

  // The SP already scopes branches to what this user may see, so the role adds
  // no further restriction: access = every branch returned for the service line.
  const accessList = useMemo(() => branchOptions.map((b) => b.label), [branchOptions]);

  const reg = service === "hh" ? HH : HOS;
  const allSections = service === "hh" ? SECTIONS_HH : SECTIONS_HOS;
  const allSnap = service === "hh" ? SNAP_HH : SNAP_HOS;
  const allHero = service === "hh" ? HERO_HH : HERO_HOS;

  const metricPillarMap = useMemo(() => {
    const m: Record<string, string> = {};
    allSections.forEach((sec) => sec.ids.forEach((id) => (m[id] = pillarKey(sec.title))));
    allSnap.ids.forEach((id) => (m[id] = "snapshot"));
    return m;
  }, [allSections, allSnap]);

  // Everything the role may see follows from the server's grants.
  const sections = allSections.filter((sec) =>
    pillarAllowed(allowedPillars, pillarKey(sec.title)),
  );
  const snapAllowed = pillarAllowed(allowedPillars, "snapshot");
  const snap = snapAllowed ? allSnap : { title: allSnap.title, ids: [] };
  const hero = allHero.filter((id) => pillarAllowed(allowedPillars, metricPillarMap[id]));

  // Every granted dashboard resolves to a spec, and every spec to a tab — there
  // is no frontend registry of renderable pages to pass first, so a dashboard
  // added server-side appears and renders on its own.
  const dashboardSpecs = useMemo(
    () => resolveDashboardSpecs(dashboards, service),
    [dashboards, service],
  );
  const specByKey = useMemo(
    () => new Map(dashboardSpecs.map((d) => [d.key, d])),
    [dashboardSpecs],
  );
  const specFor = useCallback(
    (key: string) => specByKey.get(key) ?? null,
    [specByKey],
  );
  const tabs = useMemo<PillarTab[]>(
    () => dashboardSpecs.map((d) => ({ key: d.key, label: d.label })),
    [dashboardSpecs],
  );

  // Re-resolve the active tab whenever the current one stops being available —
  // switching role is the main case, since each role grants a different set.
  // No tabs = no grants (or the config hasn't landed); the Operating View
  // renders its own empty/loading state for that, so there is no tab key to
  // fall back to.
  const activePillar = tabs.some((t) => t.key === pillar)
    ? pillar
    : tabs[0]?.key ?? "";

  const store: DaylightStore = {
    service, setService, roleId, setRoleId, branches, setBranches, payors, setPayors, episodic, setEpisodic,
    compare, setCompare, range, setRange, reg, sections, snap, hero, snapAllowed,
    allSections, allSnap, metricPillarMap,
    accessList,
    drillStack, setDrillStack,
    onDrill: (id) => setDrillStack([id]),
    closeDrill: () => setDrillStack([]),
    pillar: activePillar, setPillar,
    activeDashboard, setActiveDashboard,
    registerDetailTable, getDetailTables,

    configLoading,
    configError,
    refetchConfig,
    serviceLines,
    serviceLineSystemID,
    filterOptionsFor,
    hasFilterOptions,
    branchOptions,
    roles,
    roleName,
    dashboards,
    dashboardSpecs,
    specFor,
    allowedPillars,
    tabs,
    dataRefreshDate: config?.dataRefreshDate ?? null,
    maxDateIso,
    branchIdFor,
  };

  return <Store.Provider value={store}>{children}</Store.Provider>;
}
